import React, { createContext, useState, useEffect, useCallback } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import Config from "../config";

export const AssetContext = createContext();

// Update this mapping whenever new annual defect files are added.
// Keys are years shown in the UI and values are files inside /public/data.
const DEFECT_YEAR_FILE_MAP = Config.defectFiles;

const sortYearsDesc = (years) =>
    [...years].sort((a, b) => Number(b) - Number(a));

export const AssetProvider = ({ children }) => {
    const [assets, setAssets] = useState([]);
    const [defects, setDefectsState] = useState([]);
    const [defectsByYear, setDefectsByYear] = useState({});
    const [isDefectsLoading, setIsDefectsLoading] = useState(false);

    const availableYears = sortYearsDesc(Object.keys(DEFECT_YEAR_FILE_MAP));
    const latestYear = availableYears[0] || String(new Date().getFullYear());
    const [selectedYear, setSelectedYear] = useState(latestYear);

    const getDefectFilenameForYear = useCallback(
        (year) => {
            const normalizedYear = String(year || latestYear);
            return (
                DEFECT_YEAR_FILE_MAP[normalizedYear] ||
                DEFECT_YEAR_FILE_MAP[latestYear] ||
                "defect_list.xlsx"
            );
        },
        [latestYear],
    );

    const loadDefectDataByYear = useCallback(
        async (year) => {
            const normalizedYear = String(year || latestYear);
            const filename = getDefectFilenameForYear(normalizedYear);
            const response = await fetch(`/data/${filename}`);
            if (!response.ok) {
                throw new Error(
                    `Error loading defect file "${filename}" for year ${normalizedYear}`,
                );
            }
            const data = await response.arrayBuffer();
            const workbook = XLSX.read(data);
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const rows = XLSX.utils.sheet_to_json(worksheet);
            return rows.map((row) => {
                if (!("component" in row) && "area" in row) {
                    const { area, ...rest } = row;
                    return { ...rest, component: area };
                }
                return row;
            });
        },
        [latestYear, getDefectFilenameForYear],
    );

    const setDefects = (updatedDefects) => {
        const normalizedYear = String(selectedYear || latestYear);
        setDefectsState(updatedDefects);
        setDefectsByYear((prev) => ({
            ...prev,
            [normalizedYear]: updatedDefects,
        }));
    };

    // Function to save updated defects
    const saveDefectsToExcel = async (updatedDefects, year = selectedYear) => {
        try {
            console.log("Saving defects:", updatedDefects.length, "items");
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(updatedDefects);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Defects");

            const wbout = XLSX.write(workbook, {
                bookType: "xlsx",
                type: "array",
            });
            const blob = new Blob([wbout], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const formData = new FormData();
            formData.append("file", blob, "defect_list.xlsx");
            formData.append("year", String(year || latestYear));
            formData.append("target_filename", getDefectFilenameForYear(year));

            const response = await axios.post("/api/upload-defects", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            console.log("Save response:", response.data);
            return true;
        } catch (error) {
            console.error(
                "Error saving defect list:",
                error.response?.data || error.message,
            );
            return false;
        }
    };

    // Load data from asset_list.xlsx
    useEffect(() => {
        const loadAssetData = async () => {
            try {
                const response = await fetch("/data/asset_list.xlsx");
                if (!response.ok) {
                    throw new Error("Error loading Excel file");
                }
                const data = await response.arrayBuffer();
                const workbook = XLSX.read(data);
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                setAssets(jsonData);
            } catch (error) {
                console.error("Error loading or processing Excel file:", error);
            }
        };

        loadAssetData();
    }, []);

    // Preload latest dataset so default views always point to newest year.
    useEffect(() => {
        const loadLatestDefectData = async () => {
            try {
                setIsDefectsLoading(true);
                const latestData = await loadDefectDataByYear(latestYear);
                setDefectsByYear((prev) => ({
                    ...prev,
                    [latestYear]: latestData,
                }));
                setDefectsState(latestData);
            } catch (error) {
                console.error("Error loading latest defect list:", error);
                setDefectsState([]);
            } finally {
                setIsDefectsLoading(false);
            }
        };

        loadLatestDefectData();
    }, [latestYear, loadDefectDataByYear]);

    useEffect(() => {
        const loadSelectedYearData = async () => {
            const normalizedYear = String(selectedYear || latestYear);
            const cached = defectsByYear[normalizedYear];
            if (cached) {
                setDefectsState(cached);
                return;
            }

            try {
                setIsDefectsLoading(true);
                const yearData = await loadDefectDataByYear(normalizedYear);
                setDefectsByYear((prev) => ({
                    ...prev,
                    [normalizedYear]: yearData,
                }));
                setDefectsState(yearData);
            } catch (error) {
                console.error(
                    `Error loading defect list for year ${normalizedYear}:`,
                    error,
                );
                setDefectsState([]);
            } finally {
                setIsDefectsLoading(false);
            }
        };

        loadSelectedYearData();
    }, [selectedYear, latestYear, defectsByYear, loadDefectDataByYear]);

    const latestDefectIds = new Set(
        (defectsByYear[latestYear] || []).map((defect) =>
            String(defect.defect_id || ""),
        ),
    );

    return (
        <AssetContext.Provider
            value={{
                assets,
                defects,
                setDefects,
                saveDefectsToExcel,
                selectedYear,
                setSelectedYear,
                availableYears,
                latestYear,
                latestDefectIds,
                isDefectsLoading,
            }}
        >
            {children}
        </AssetContext.Provider>
    );
};
